import { PlusCircleIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { getJwt } from "@/features/auth/utils";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { companyDetails, MandateService } from "@/features/company/types";
import { Routes } from "@/features/router";
import { useAliasMobile } from "@/features/user/hooks/use-alias-mobile";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { UserType } from "@/features/user/types";
import { ModalAddMobile } from "@/molecules/modal-add-mobile/index.react";
import { ModalDeleteAlias } from "@/molecules/modal-delete-alias/index.react";
import { PhoneCardV2 } from "@/molecules/phone-card/phone-card-v2";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setAddAlias } from "@/redux/slices/companyOnboardingSlice";
import { AliasMobileSchema } from "@/types/user/alias-mobile.types";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { LayoutTitle } from "../../../_layout/index.react";

type AliasesMobileProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

export const AliasMobilePage = ({ isOnboarding = false, ...props }: AliasesMobileProps) => {
  const translation = useTranslation(["pass", "atoms", "error"]);
  const { t } = translation;
  const navigate = useNavigate();
  const { leUuid } = useCompanyContext();
  const dispatch = useAppDispatch();

  // UseQuery profile and alias-mobile
  const { useUserProfile } = useUser();
  const { data: user, refetch } = useUserProfile({
    enabled: !leUuid,
    queryKey: ["user", "profile"]
  });
  //
  const { useGetAliasMobile, useDeleteAliasMobile, useCreateAliasMobile } = useAliasMobile();
  const { data: aliasMobileData, isLoading: aliasMobileLoading } = useGetAliasMobile({
    enabled: !!getJwt()
  });
  const { mutateAsync: createAliasMobile } = useCreateAliasMobile();
  const { mutateAsync: deleteAliasMobile, isPending: deleteAliasMobileIsPending } =
    useDeleteAliasMobile();
  //

  // UseQuery companyDetail
  const { companyDetailQuery } = useCompany();
  const { data: companyDetails } = companyDetailQuery(leUuid);
  //

  // States to handle modals //
  const { checkPermission } = useUserCheckAccess();
  const [searchParams] = useSearchParams();
  const addMobileInOnboarding = useAppSelector((state) => state.companyOnboarding.addAlias);
  const [modalAddMobile, setModalAddMobile] = useState<boolean>(
    !!searchParams.get("add") || addMobileInOnboarding
  );
  const [deletedMobile, setDeletedMobile] = useState<AliasMobileSchema | null>(null);

  useEffect(() => {
    if (!addMobileInOnboarding) return;
    dispatch(setAddAlias(false));
  }, [addMobileInOnboarding]);

  // Business rules
  const isCompanyEditable = (companyDetails: companyDetails | undefined): boolean => {
    return (
      !companyDetails?.legalEntity.UserValidation &&
      checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails)
    );
  };

  const isUserVerified = (user: UserType | undefined): boolean => {
    return (
      user?.olkyPassLevel.olkypass_status.level != null &&
      user.olkyPassLevel.olkypass_status.level < 2
    );
  };

  const isDeletionEnabled = (
    user: UserType | undefined,
    leUuid: string | null,
    companyDetails: companyDetails | undefined
  ) => {
    return leUuid ? isCompanyEditable(companyDetails) : isUserVerified(user);
  };
  const enabled: boolean = isDeletionEnabled(user, leUuid, companyDetails ?? undefined);
  //

  // Events
  const onDelete = async () => {
    if (!deletedMobile || !enabled) return;
    try {
      await deleteAliasMobile({ uuid: deletedMobile.uuid });
    } catch (error: unknown) {
      toast.error(extractErrorDescription(error, translation));
    }
    setDeletedMobile(null);
  };

  return (
    <Column {...props}>
      {!isOnboarding && <LayoutTitle className="sm:mb-0">{t("aliasMobilePage.title")}</LayoutTitle>}
      <div className="mb-4 flex w-full justify-start">
        <Button
          dataCy="button-add-mobile"
          className="mt-4 w-auto sm:mt-0"
          onClick={() => setModalAddMobile(true)}
          disabled={!enabled || aliasMobileLoading}
          dataTooltip={!enabled && !leUuid ? t("aliasMobilePage.button.tooltip.create") : undefined}
          dataTooltipPlace="bottom"
          icon={<PlusCircleIcon />}
        >
          {t("aliasMobilePage.button.add_mobile")}
        </Button>
      </div>

      <ModalAddMobile
        open={modalAddMobile}
        onClose={() => setModalAddMobile(false)}
        onValidated={async (mobile) => {
          try {
            await createAliasMobile({
              main: mobile.main,
              mobileCode: mobile.MobileIndicatif,
              phoneNumber: mobile.MobileNumber,
              public: false
            });
            toast.success(t("success.create"));
          } catch (error: unknown) {
            toast.error(extractErrorDescription(error, translation));
          }
          setModalAddMobile(false);
          refetch();
        }}
      />

      <ModalDeleteAlias
        isOpen={!!deletedMobile}
        onClose={() => setDeletedMobile(null)}
        onValidated={onDelete}
        isConfirmLoading={deleteAliasMobileIsPending}
        disabled={!enabled}
      >
        <Base>{t("aliasMobilePage.confirm_delete", { name: deletedMobile?.phoneNumber })}</Base>
      </ModalDeleteAlias>

      <div className={"flex max-w-3xl flex-col"}>
        {aliasMobileLoading ? (
          <Loader className="mt-5 ml-10" />
        ) : (
          aliasMobileData &&
          aliasMobileData.map((mobile) => (
            <PhoneCardV2
              key={mobile.uuid}
              onPublicClick={() => navigate(Routes.account_alias_public)}
              disabled={!enabled}
              onDeleteHelper={
                !enabled && !leUuid ? t(t("aliasMobilePage.button.tooltip.delete")) : undefined
              }
              aliasMobile={mobile}
              onDelete={() => {
                if (mobile.main) return;
                if (aliasMobileData.length > 1 && enabled) {
                  setDeletedMobile(mobile);
                } else if (!(aliasMobileData.length > 1)) {
                  toast.error(t("aliasMobilePage.forbidden_remove"));
                } else {
                  toast.error(t("aliasMobilePage.forbidden_remove"));
                }
              }}
            />
          ))
        )}
      </div>
    </Column>
  );
};
